import {
  CreditLineDetailSchema,
  CreditLineListResponseSchema,
  CreditMovementPageSchema,
  CreditPaymentSchema,
  CustomerStatementSchema,
  DrawdownSchema,
  SimulationResultSchema,
  StatementSchema,
  type CreditPaymentType,
  type MovementType,
} from '@paguz/contracts';
import { z } from 'zod';
import { filenameFrom, type DownloadedFile } from '../lib/files.ts';
import { apiFetch, apiRequest } from './client.ts';

/**
 * Crédito (`/v1/credit/*`). Las cifras (cuotas, intereses, mínimos) vienen siempre de la API
 * (Credit Core); aquí solo se piden y validan.
 */

/** `GET /v1/credit/lines/{id}/drawdowns` → `{ data }` (envoltorio aún no publicado en contratos). */
export const DrawdownListSchema = z.object({ data: z.array(DrawdownSchema) });
/** `GET /v1/credit/lines/{id}/statements` → `{ data }` (envoltorio aún no publicado en contratos). */
export const StatementListSchema = z.object({ data: z.array(StatementSchema) });

export function listLines() {
  return apiRequest('/v1/credit/lines', CreditLineListResponseSchema);
}

export function getLine(lineId: string) {
  return apiRequest(`/v1/credit/lines/${encodeURIComponent(lineId)}`, CreditLineDetailSchema);
}

export function listDrawdowns(lineId: string) {
  return apiRequest(`/v1/credit/lines/${encodeURIComponent(lineId)}/drawdowns`, DrawdownListSchema);
}

export const MOVEMENT_FILTERS = {
  ALL: { label: 'Todos', types: [] },
  PURCHASE: { label: 'Compras', types: ['PURCHASE'] },
  PAYMENT: { label: 'Pagos', types: ['PAYMENT'] },
  REFUND: { label: 'Devoluciones', types: ['REFUND', 'REVERSAL'] },
} as const satisfies Record<string, { label: string; types: readonly MovementType[] }>;
export type MovementFilter = keyof typeof MOVEMENT_FILTERS;

export function listMovements(
  lineId: string,
  options: { filter: MovementFilter; cursor?: string | undefined; limit?: number },
) {
  return apiRequest(
    `/v1/credit/lines/${encodeURIComponent(lineId)}/movements`,
    CreditMovementPageSchema,
    {
      query: {
        type: MOVEMENT_FILTERS[options.filter].types.join(','),
        cursor: options.cursor,
        limit: options.limit ?? 20,
      },
    },
  );
}

export function listStatements(lineId: string) {
  return apiRequest(
    `/v1/credit/lines/${encodeURIComponent(lineId)}/statements`,
    StatementListSchema,
  );
}

/** Extracto del corte con la identificación legal de la entidad (NIT, Defensor). */
export function getStatement(lineId: string, statementId: string) {
  return apiRequest(
    `/v1/credit/lines/${encodeURIComponent(lineId)}/statements/${encodeURIComponent(statementId)}`,
    CustomerStatementSchema,
  );
}

/** Extracto en PDF (formato bancario) generado por la API: `GET …/statements/{id}.pdf`. */
export async function downloadStatementPdf(
  lineId: string,
  statementId: string,
): Promise<DownloadedFile> {
  const response = await apiFetch(
    `/v1/credit/lines/${encodeURIComponent(lineId)}/statements/${encodeURIComponent(statementId)}.pdf`,
    { accept: 'application/pdf' },
  );
  return {
    blob: await response.blob(),
    filename: filenameFrom(response, `extracto-${statementId}.pdf`),
  };
}

/** `POST /v1/credit/simulations` (Credit Core). `amount` como string decimal. */
export function simulate(
  input: { lineId: string; amount: string; installments: number },
  signal?: AbortSignal,
) {
  return apiRequest('/v1/credit/simulations', SimulationResultSchema, {
    method: 'POST',
    body: {
      lineId: input.lineId,
      amount: { amount: input.amount, currency: 'COP' },
      installments: input.installments,
    },
    ...(signal !== undefined && { signal }),
  });
}

/** `POST /v1/credit/payments` (Idem ✔) → `REQUIRES_SCA` + `scaChallengeId`. */
export function createCreditPayment(
  input: { lineId: string; amount: string; type: CreditPaymentType; sourceAccountId: string },
  idempotencyKey: string,
) {
  return apiRequest('/v1/credit/payments', CreditPaymentSchema, {
    method: 'POST',
    idempotencyKey,
    body: {
      lineId: input.lineId,
      amount: { amount: input.amount, currency: 'COP' },
      type: input.type,
      sourceAccountId: input.sourceAccountId,
    },
  });
}

/** `POST /v1/credit/payments/{id}/confirm` (Idem ✔) con el `scaToken`. */
export function confirmCreditPayment(paymentId: string, scaToken: string, idempotencyKey: string) {
  return apiRequest(
    `/v1/credit/payments/${encodeURIComponent(paymentId)}/confirm`,
    CreditPaymentSchema,
    { method: 'POST', idempotencyKey, body: { scaToken } },
  );
}
